import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import "../../../styles/payrole-system/payrole-forms.css";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { getMonthYear } from "../../common-data/Converts";
import DiscrepancyForm from "./DiscrepancyForm";
import EmailForm from "./EmailForm";
import ParoleInfoForm from "./ParoleInfoForm";
import { getFormLabels } from "../PayroleMocks";
import { CheckIcon, EmailIcon, XIcon } from "../../../images/icons/icons";

const PayroleApprovalForm = ({
  userInfoData,
  payrollData,
  approvalType,
  selectedDraftIds = [],
  allDatas = {},
  onClose,
  callFunction,
}) => {
  const dispatch = useDispatch();
  const { postPutLoading } = useSelector((state) => state.common);

  const initialFormValues = {
    action: "",
    remarks: "",
  };
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [successAction, setSuccessAction] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // const [currentMonth, setCurrentMonth] = useState(() => {
  //   return payrollData?.date ? new Date(payrollData.date) : new Date();
  // });
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateDiscrepancies, setDateDiscrepancies] = useState({});

  const validateForm = (values) => {
    const errors = {};

    if (approvalType !== "send_email" && approvalType !== "bulk_send_email") {
      if (!values.action) {
        const isStaff = approvalType === "staff";
        const actionLabels = isStaff
          ? "Review Done/Create Discrepancy"
          : "Approve/Reject";
        errors.action = `Please select an option (${actionLabels})!`;
      }
    }

    const showMainRemarks =
      (approvalType !== "send_email" &&
        approvalType !== "bulk_send_email" &&
        approvalType !== "staff") ||
      (approvalType === "staff" && values.action !== "reject");

    if (
      // approvalType !== "send_email" &&
      showMainRemarks
    ) {
      if (!values.remarks || values.remarks.trim().length === 0) {
        errors.remarks = "Remarks are required!";
      } else if (values.remarks.trim().length < 10) {
        errors.remarks = "Remarks must be at least 10 characters!";
      }
    }

    if (approvalType === "staff" && values.action === "reject") {
      if (selectedDates.length === 0) {
        errors.selectedDates =
          "Please select at least one date from the calendar!";
      } else {
        const incompleteDates = selectedDates.filter((dateString) => {
          const dateData = dateDiscrepancies[dateString];
          return (
            !dateData ||
            !dateData.discrepancyTypes ||
            dateData.discrepancyTypes.trim() === "" ||
            !dateData.remarks ||
            dateData.remarks.trim().length === 0 ||
            !dateData.proofImages ||
            dateData.proofImages.length === 0
          );
        });

        if (incompleteDates.length > 0) {
          errors.discrepancyTypes = `Please select a discrepancy type, add remarks, and upload at least one proof image for all selected dates!`;
        }
      }
    }

    return errors;
  };

  const scrollToFirstError = (errors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) return;

    const fieldOrder = [
      "action",
      "discrepancyTypes",
      "selectedDates",
      "remarks",
    ];

    let firstErrorField = null;
    for (const field of fieldOrder) {
      if (errors[field]) {
        firstErrorField = field;
        break;
      }
    }

    if (!firstErrorField) {
      firstErrorField = errorFields[0];
    }

    let targetElement = null;

    switch (firstErrorField) {
      case "action":
        targetElement = document.querySelector(".pp-approval-actions-section");
        break;
      case "discrepancyTypes":
        targetElement = document.querySelector(".pp-discrepancy-types-section");
        break;
      case "selectedDates":
        targetElement = document.querySelector(".pp-attendance-calendar-card");
        break;
      case "remarks":
        targetElement = document.querySelector(".pp-approval-remarks-section");
        break;
      default:
        targetElement = document.querySelector(".pp-approval-form");
    }

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      setTimeout(() => {
        const focusableElement = targetElement.querySelector(
          "button, input, textarea, select"
        );
        if (focusableElement) {
          focusableElement.focus();
        }
      }, 500);
    }
  };

  const handleRemarksChange = (e) => {
    setFormValues((prev) => ({
      ...prev,
      remarks: e.target.value,
    }));
  };

  const handleCreateDiscrepancy = async () => {
    if (selectedDates.length === 0) {
      return;
    }

    const discrepancyArray = selectedDates.map((dateString) => {
      const dateData = dateDiscrepancies[dateString] || {};
      const date = moment.tz(dateString, "Asia/Kolkata");

      return {
        discrepancy_type: dateData.discrepancyTypes || "",
        date: date.format("YYYY-MM-DD"),
        description: dateData.remarks || "",
        proof_img: (dateData.proofImages || [])
          .map((img) => img.url)
          .filter(Boolean),
      };
    });

    const discrepancyPayload = {
      user_id: payrollData?.user_id || "",
      payrole_id: payrollData?.uid || "",
      discrepancy: discrepancyArray,
    };

    try {
      const result = await dispatch(
        commonPostPutApi({
          method: "POST",
          endpoint: "/payrole/discrepancy",
          payload: discrepancyPayload,
          token: userInfoData?.token || "",
        })
      );

      if (result?.payload?.status) {
        console.log("Discrepancy created successfully");
      } else {
        console.error(
          "Failed to create discrepancy:",
          result?.payload?.message
        );
      }
    } catch (error) {
      console.error("Error creating discrepancy:", error);
    }
  };

  const handleSubmitForm = async (payload) => {
    const method =
      approvalType === "send_email" || approvalType === "bulk_send_email"
        ? "POST"
        : "PUT";
    let endpoint;

    if (approvalType === "staff") {
      endpoint = "/payrole/user-approval";
    } else if (
      approvalType === "send_email" ||
      approvalType === "bulk_send_email"
    ) {
      endpoint = "/payrole/request-approval";
    } else {
      endpoint = "/payrole/stack-holder-approval";
    }

    const token = userInfoData?.token || "";

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        if (
          approvalType === "send_email" ||
          approvalType === "bulk_send_email"
        ) {
          setSuccessAction("email_sent");
          setSuccessMessage(
            result?.payload?.message || "Email sent successfully!"
          );
        } else {
          setSuccessAction(payload.status);
          setSuccessMessage(result?.payload?.message || "Success!");
        }

        if (approvalType === "staff" && payload.status === "rejected") {
          await handleCreateDiscrepancy();
        }

        setFormValues(initialFormValues);
        setShowSuccess(true);
        callFunction();
        toast.success(result?.payload?.message || "Success!");
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error making POST request:", error);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm(formValues);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      const payload = {
        user_id: userInfoData?.user_id || userInfoData?.uid || "",
        payrole_id: payrollData?.uid || "",
        status: formValues.action === "approve" ? "approved" : "rejected",
        remarks: formValues.remarks || "",
      };

      const emailPayload = {
        payroleIds:
          approvalType === "bulk_send_email"
            ? selectedDraftIds
            : [payrollData?.uid],
      };

      const actionText = formValues.action === "approve" ? "approve" : "reject";
      const payrollMonth = payrollData?.date
        ? getMonthYear(payrollData.date)
        : "-";

      const handleConfirm = async () => {
        handleSubmitForm(
          approvalType === "send_email" || approvalType === "bulk_send_email"
            ? emailPayload
            : payload
        );
      };

      showConfirmAlert(
        handleConfirm,
        approvalType === "send_email"
          ? `Are you sure you want to send an email to the ${payrollData?.name} for payroll ${payrollMonth}?`
          : approvalType === "bulk_send_email"
          ? `Are you sure you want to send emails to ${selectedDraftIds.length} selected payroll(s)?`
          : `Are you sure you want to ${actionText} this payroll for ${payrollMonth}? This action cannot be undone.`,
        "No, Cancel",
        `Yes, Submit`
      );
    } else {
      scrollToFirstError(errors);
    }
  };

  // useEffect(() => {
  //   if (payrollData?.date) {
  //     setCurrentMonth(new Date(payrollData.date));
  //   }
  // }, [payrollData?.date]);

  if (showSuccess) {
    return (
      <div className="pp-approval-container">
        <div className="pp-approval-success">
          <div className="pp-approval-success-icon">
            {successAction === "approved" ? (
              <CheckIcon color="#16a34a" />
            ) : successAction === "email_sent" ? (
              <EmailIcon color="#0369a1" />
            ) : (
              <XIcon color="#cd0909" />
            )}
          </div>
          <div className="pp-approval-success-title">
            {successAction === "approved"
              ? "Payroll Approved Successfully!"
              : successAction === "email_sent"
              ? "Email Sent Successfully!"
              : "Payroll Rejected Successfully!"}
          </div>
          <div className="pp-approval-success-desc">{successMessage}</div>
          <button
            className="pp-approval-success-btn"
            onClick={() => {
              setSuccessAction("");
              setSuccessMessage("");
              onClose();
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  console.log("formErrors=> ", formErrors);

  return (
    <div className="pp-approval-container">
      <div className="pp-approval-overlay" onClick={onClose}></div>
      <form className="pp-approval-form">
        <div className="pp-approval-header">
          <h3 className="pp-approval-title">
            {approvalType === "bulk_send_email"
              ? `Send Bulk Email (${selectedDraftIds.length} payrolls)`
              : getFormLabels(approvalType)}
          </h3>

          <button
            type="button"
            className="pp-approval-close-btn"
            onClick={onClose}
          >
            X
          </button>
        </div>

        <div className="pp-approval-content">
          {approvalType === "bulk_send_email" ? (
            <div className="pp-bulk-email-info-card">
              <div className="pp-bulk-email-info-header">
                <span className="pp-bulk-email-icon">
                  <EmailIcon color="#0369a1" />
                </span>
                <h4 className="pp-bulk-email-title">Bulk Email Information</h4>
              </div>
              <div className="pp-bulk-email-info-content">
                <p className="pp-bulk-email-description">
                  You are about to send approval request emails to{" "}
                  <strong>{selectedDraftIds.length}</strong> selected
                  payroll(s).
                </p>
                <div className="pp-bulk-email-details">
                  <h5 className="pp-bulk-email-list-title">
                    Selected Payrolls:
                  </h5>
                  <div className="pp-bulk-email-list">
                    {selectedDraftIds.map((payrollId, index) => {
                      const payrollData = allDatas?.draftPayrollData?.find(
                        (p) => p.uid === payrollId
                      );
                      const employeeName =
                        payrollData?.name || "Unknown Employee";
                      const payrollMonth = payrollData?.date
                        ? getMonthYear(payrollData.date)
                        : "Unknown Month";

                      return (
                        <div
                          key={payrollId}
                          className="pp-bulk-email-list-item"
                        >
                          <span className="pp-bulk-email-employee-name">
                            {employeeName}
                          </span>
                          <span className="pp-bulk-email-month">
                            {payrollMonth}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ParoleInfoForm payrollData={payrollData} />
          )}

          {approvalType !== "send_email" &&
            approvalType !== "bulk_send_email" && (
              <EmailForm
                formValues={formValues}
                setFormValues={setFormValues}
                formErrors={formErrors}
                isStaff={approvalType === "staff"}
              />
            )}

          {approvalType === "staff" && formValues.action === "reject" && (
            <DiscrepancyForm
              userInfoData={userInfoData}
              approvalType={approvalType}
              formValues={formValues}
              payrollData={payrollData}
              formErrors={formErrors}
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
              // currentMonth={currentMonth}
              // discrepancyOptions={discrepancyOptions}
              dateDiscrepancies={dateDiscrepancies}
              setDateDiscrepancies={setDateDiscrepancies}
            />
          )}

          {(() => {
            const showMainRemarks =
              (approvalType !== "send_email" &&
                approvalType !== "bulk_send_email" &&
                approvalType !== "staff") ||
              (approvalType === "staff" && formValues.action !== "reject");

            return (
              // approvalType !== "send_email" &&
              showMainRemarks && (
                <div className="pp-approval-remarks-section">
                  <label
                    htmlFor="remarks"
                    className="pp-approval-section-label"
                  >
                    Remarks <span className="pp-approval-required">*</span>
                  </label>

                  <textarea
                    id="remarks"
                    name="remarks"
                    value={formValues.remarks}
                    onChange={handleRemarksChange}
                    placeholder="Enter your remarks for this decision..."
                    className={`pp-approval-remarks-textarea ${
                      formErrors?.remarks ? "formError" : ""
                    }`}
                    rows={4}
                    maxLength={500}
                  />

                  <div className="pp-approval-remarks-footer">
                    <span className="pp-approval-remarks-counter">
                      {formValues.remarks.length}/500 characters
                    </span>
                  </div>

                  {formErrors?.remarks && (
                    <span className="pp-approval-error">
                      {formErrors.remarks}
                    </span>
                  )}
                </div>
              )
            );
          })()}

          <div className="pp-approval-form-actions">
            <button
              type="button"
              className="pp-approval-btn cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`pp-approval-btn submit ${
                approvalType === "send_email" ||
                approvalType === "bulk_send_email"
                  ? "send-email"
                  : formValues.action
              }`}
              onClick={onSubmit}
              disabled={postPutLoading}
            >
              {postPutLoading
                ? "Processing..."
                : approvalType === "bulk_send_email"
                ? `Send Bulk Email (${selectedDraftIds.length})`
                : "Submit"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PayroleApprovalForm;
